/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: rodeos entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the rodeos table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the rodeos API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, name, description, start_date, end_date, primary_organization_id, url, slack_time, metadata, is_published, rodeo_or_jackpot, default_handicap_organization_id, created_by, created_at, updated_by, updated_at, buddy_groups_enabled, max_buddy_group_size, member_only_buddy_groups, duplicate_detection_mode, allow_non_member_buddy_group, non_member_surcharge, allow_cash_payment, preference_config, entry_close_time, fees_due_message, location, timezone, draw_mode, scoring_state, scoring_went_live_at, scoring_went_live_by, member_fee_enabled, allow_pay_later, cancelled_at, cancelled_by, cancellation_reason, is_finals]
 * Searchable fields: []
 * Filterable fields: [id, primary_organization_id, default_handicap_organization_id]
 */

import { httpClient } from '../http-client.js';
import type { UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  RodeosRow,
  RodeosInsert,
  RodeosUpdate
} from '../../types/generated_schemas/generated-rodeos-schema.js';

export interface RodeosQueryOptions extends UnifiedQueryOptions {}

export interface RodeosResponse {
  data: RodeosRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for rodeos table operations
 *
 * @description Provides type-safe access to rodeos API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class RodeosClient {
  private readonly basePath = '/rodeos';

  /**
   * Get all rodeos records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of rodeos records
   */
  async getAll(options?: RodeosQueryOptions): Promise<RodeosRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<RodeosResponse>(url);
    return response.data;
  }

  /**
   * Get a single rodeos record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the rodeos record
   */
  async getById(id: string): Promise<RodeosRow> {
    const response = await httpClient.get<{
      data: RodeosRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new rodeos record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created rodeos record
   */
  async create(data: RodeosInsert): Promise<RodeosRow> {
    const response = await httpClient.post<{
      data: RodeosRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing rodeos record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated rodeos record
   */
  async update(id: string, data: RodeosUpdate): Promise<RodeosRow> {
    const response = await httpClient.put<{
      data: RodeosRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a rodeos record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple rodeos records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created rodeos records
   */
  async createMany(data: RodeosInsert[]): Promise<RodeosRow[]> {
    const response = await httpClient.post<{
      data: RodeosRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple rodeos records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search rodeos with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: RodeosQueryOptions
  ): Promise<RodeosResponse> {
    throw new Error('Search operations are not available for rodeos - no searchable fields configured');
  }

  /**
   * Get count of rodeos records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a rodeos record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'rodeos',
      publicFields: ['id', 'name', 'description', 'start_date', 'end_date', 'primary_organization_id', 'url', 'slack_time', 'metadata', 'is_published', 'rodeo_or_jackpot', 'default_handicap_organization_id', 'created_by', 'created_at', 'updated_by', 'updated_at', 'buddy_groups_enabled', 'max_buddy_group_size', 'member_only_buddy_groups', 'duplicate_detection_mode', 'allow_non_member_buddy_group', 'non_member_surcharge', 'allow_cash_payment', 'preference_config', 'entry_close_time', 'fees_due_message', 'location', 'timezone', 'draw_mode', 'scoring_state', 'scoring_went_live_at', 'scoring_went_live_by', 'member_fee_enabled', 'allow_pay_later', 'cancelled_at', 'cancelled_by', 'cancellation_reason', 'is_finals'],
      searchableFields: [],
      filterableFields: ['id', 'primary_organization_id', 'default_handicap_organization_id'],
      sortableFields: ['start_date', 'end_date', 'created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at'],
      requiredFields: ['id', 'buddy_groups_enabled', 'max_buddy_group_size', 'member_only_buddy_groups', 'duplicate_detection_mode', 'allow_non_member_buddy_group', 'allow_cash_payment', 'entry_close_time', 'timezone', 'draw_mode', 'scoring_state', 'allow_pay_later', 'is_finals'],
    };
  }
}

// Export singleton instance for convenience
export const rodeosClient = new RodeosClient();
